import { post } from "@src/appV2/api/parsedApi";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { z } from "zod";

const bodySchema = z.object({
  data: z.object({
    type: z.literal("step-up-elevation"),
    attributes: z.object({
      freshIdToken: z.string().min(1),
    }),
  }),
});

const responseSchema = z.object({
  data: z.object({
    type: z.literal("step-up-elevation"),
    id: z.string(),
    attributes: z.object({
      elevatedUntil: z.string(),
    }),
  }),
});

type ElevateResponse = z.infer<typeof responseSchema>;

/**
 * Grants a PHI elevation window on home-health-api by proving a fresh side-channel
 * re-auth (see api/cognitoSideChannel.ts). Authenticated with the CURRENT session token —
 * the endpoint verifies the fresh token belongs to the same user before elevating.
 */
export function useElevateStepUp(): UseMutationResult<ElevateResponse, unknown, string> {
  return useMutation({
    mutationFn: async (freshIdToken: string) => {
      const response = await post({
        url: `${environmentConfig.REACT_APP_HOME_HEALTH_API_URL}/v1/step-up/elevate`,
        data: {
          data: {
            type: "step-up-elevation" as const,
            attributes: { freshIdToken },
          },
        },
        bodySchema,
        responseSchema,
      });
      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.STEP_UP_ELEVATE_FAILURE,
    },
  });
}
